import React, { useEffect, useState } from 'react';

import PostField from 'components/feed/PostField';
import { useHistory } from 'react-router-dom';
import { addMention } from 'screens/home/addPost/mentionHelper';
import styled, { css } from 'styled-components';
import { commentEditorStyles, commentPlaceholderStyles } from 'styles/common';

import { SEARCH_WIDGET_TYPES } from 'constants/ProjectConstants';
import { getQueryParams } from 'constants/helper';

import { getApplicableCompetenciesForEmployees } from 'services/admin';

const PostFieldContainer = styled.div`
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 4px;
  ${(props) =>
    props.loading &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `};

  ${(props) =>
    props.customStyles &&
    css`
      ${props.$customStyles};
    `};
`;

const getRelativeParent = function getRelativeParent(element) {
  if (!element) {
    return null;
  }

  const position = window.getComputedStyle(element).getPropertyValue('position');
  if (position !== 'static') {
    return element;
  }

  return getRelativeParent(element.parentElement);
};

const FeedbackComment = ({
  editorState,
  onEnter,
  loading,
  updateEditorState,
  onEscape,
  TagRecommendations,
  placeholder,
  customCSS,
  setHasError,
  setErrorMessage,
  useMentionFunctionality = true,
  selectedEmployees = [], // <- this is from ShareFeedback.js
}) => {
  const history = useHistory();
  const { location } = history;
  const queryString = location.search;

  const selectedEmployeeIds = selectedEmployees?.map(({ id }) => id) ?? [];

  const { competencyId, empId } = getQueryParams(queryString);

  const employeeIds = empId ? [...selectedEmployeeIds, empId] : [...selectedEmployeeIds];

  const [competencyTag, setCompetencyTag] = useState(null);

  const fetchCompetencyTag = async () => {
    try {
      const res = await getApplicableCompetenciesForEmployees({
        competencyIds: [competencyId],
        employeeIds: [empId],
      });
      const competency = res[0];
      setCompetencyTag({ competency });
    } catch (err) {
      console.log('Error fetching competency : ', err);
    }
  };

  useEffect(() => {
    if (competencyId) {
      fetchCompetencyTag();
    }
  }, [competencyId, queryString, empId]);

  useEffect(() => {
    // setting it in another useffect as it's not working in the upper useffect, some weird quirk with draftjs
    if (competencyTag?.competency) {
      const newEditorState = addMention(editorState, competencyTag?.competency, ' #', '#', true);
      updateEditorState(newEditorState);
    }
  }, [competencyTag]);

  return (
    <PostFieldContainer loading={loading} $customStyles={customCSS}>
      <PostField
        placeholder={placeholder || 'Type Your Feedback Here*'}
        height="5rem"
        updateEditorState={updateEditorState}
        editorState={editorState}
        editorStyles={commentEditorStyles}
        placeholderStyles={commentPlaceholderStyles}
        useMoneyFunctionality
        onEnter={onEnter}
        widgetType={SEARCH_WIDGET_TYPES.comment}
        createNewTagFunctionality={false}
        onEscape={onEscape}
        allowNewLine
        setHasError={setHasError}
        setErrorMessage={setErrorMessage}
        useMentionFunctionality={useMentionFunctionality}
        mentionedEmployeeIds={[...new Set(employeeIds)]}
        positionSuggestions={(_ref) => {
          const { decoratorRect } = _ref;
          const { popover } = _ref;
          const { state } = _ref;
          const { props } = _ref;

          const relativeParent = getRelativeParent(popover.parentElement);
          const relativeRect = {};

          if (relativeParent) {
            relativeRect.scrollLeft = relativeParent.scrollLeft;
            relativeRect.scrollTop = relativeParent.scrollTop;

            const relativeParentRect = relativeParent.getBoundingClientRect();
            relativeRect.left = decoratorRect.left - relativeParentRect.left;
            relativeRect.top = decoratorRect.bottom - relativeParentRect.top;
          } else {
            relativeRect.scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            relativeRect.scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

            relativeRect.top = decoratorRect.bottom;
            relativeRect.left = decoratorRect.left;
          }

          const left = relativeRect.left + relativeRect.scrollLeft;
          const top = relativeRect.top + relativeRect.scrollTop;

          let transform;
          let transition;
          if (state.isActive) {
            if (props.suggestions.length > 0) {
              transform = 'scale(1)';
              transition = 'all 0.25s cubic-bezier(.3,1.2,.2,1)';
            } else {
              transform = 'scale(0)';
              transition = 'all 0.35s cubic-bezier(.3,1,.2,1)';
            }
          }

          return {
            left: `${left + 35}px`,
            top: `${top + 400}px`,
            transform,
            transformOrigin: '1em 0%',
            transition,
          };
        }}
        popperOptions={{
          placement: 'bottom-start',
        }}
      />
      {TagRecommendations}
    </PostFieldContainer>
  );
};

export default FeedbackComment;
